import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type CSSPropertyName,
  processTextElements
} from '../utils/process-text-elements';

const DEBOUNCE_DELAY = 100;
const TEXT_PROPERTIES: CSSPropertyName[] = [
  'fontSize',
  'lineHeight',
  'letterSpacing'
];

export function useTextStyleAttributes() {
  const [processedElements, setProcessedElements] = useState<HTMLElement[]>([]);
  const elementsSetRef = useRef(new Set<HTMLElement>());
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const updateProcessedElements = useCallback((nodes: Node[]) => {
    if (nodes.length === 0) return;

    const previousElements = new Set(elementsSetRef.current);

    processTextElements(nodes, TEXT_PROPERTIES, elementsSetRef.current);

    const hasNewElements = Array.from(elementsSetRef.current).some(
      (element) => !previousElements.has(element)
    );

    if (hasNewElements) {
      setProcessedElements(Array.from(elementsSetRef.current));
    }
  }, []);

  useEffect(() => {
    // Initial processing of all elements
    updateProcessedElements(Array.from(document.body.querySelectorAll('*')));

    // Watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        const nodesToProcess = collectNodesToProcess(mutations);
        updateProcessedElements(nodesToProcess);
      }, DEBOUNCE_DELAY);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      clearTimeout(debounceTimerRef.current);
      observer.disconnect();
    };
  }, [updateProcessedElements]);

  return processedElements;
}

function collectNodesToProcess(mutations: MutationRecord[]): HTMLElement[] {
  const nodes: HTMLElement[] = [];
  const processedTargets = new Set<HTMLElement>();

  mutations.forEach((mutation) => {
    if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
      return;
    }

    // Process parent element (handles dangerouslySetInnerHTML with text nodes)
    if (
      mutation.target instanceof HTMLElement &&
      !processedTargets.has(mutation.target)
    ) {
      nodes.push(mutation.target);
      processedTargets.add(mutation.target);
    }

    // Process added elements and their descendants
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        nodes.push(node, ...node.querySelectorAll<HTMLElement>('*'));
      }
    });
  });

  return nodes;
}
